import { Button } from '@/components/ui/button';
import * as Field from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import * as RadioGroup from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toaster } from '@/components/ui/toast';
import VideoPlayer from '@components/VideoPlayer';
import { createForm, getValue, setValue, valiForm } from '@modular-forms/solid';
import { userId } from '@state/auth';
import { useMutation } from '@tanstack/solid-query';
import { CheckIcon } from 'lucide-solid';
import { For, Show, createSignal, onCleanup } from 'solid-js';
import { useService } from 'solid-services';
import { queryClient } from 'src/App';
import { TOPICS, TOPIC_KEYS } from '@features/communityContent/topics';
import { Box } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';
import { detectVideoFromUrl } from '../../detectVideoUrl';
import { fetchThumbnailUrl } from '../../fetchThumbnail';
import { useState } from '../state';
import { saveVideo, updateVideo } from './api';
import { type VideoForm, VideoSchema } from './videoSchema';

export default function (props: { onSuccess: () => void }) {
  const { editVideo, setEditVideo } = useService(useState)();
  const isEdit = () => !!editVideo();

  const [url, setUrl] = createSignal('');
  const [detecting, setDetecting] = createSignal(false);

  const mutation = useMutation(() => ({
    mutationFn: (values: VideoForm) => {
      const existing = editVideo();
      if (existing) {
        return updateVideo({ id: existing.id, values });
      }
      return saveVideo(values);
    },
    onSuccess: () => {
      toaster.create({
        title: isEdit() ? 'Video updated' : 'Video submitted',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['go-watch-videos', userId()] });
      setEditVideo(undefined);
      props.onSuccess();
    },
    onError: (error) => {
      toaster.create({
        title: 'Error saving video',
        description: error.message,
        type: 'error',
      })
    },
  }));

  const [videoForm, { Form, Field: FormField }] = createForm<VideoForm>({
    validate: valiForm(VideoSchema),
    initialValues: {
      title: editVideo()?.title ?? '',
      topic: editVideo()?.topic ?? 'finance',
      provider: editVideo()?.provider ?? undefined,
      provider_id: editVideo()?.provider_id ?? '',
      thumbnail_url: editVideo()?.thumbnail_url ?? undefined,
      description: editVideo()?.description ?? '',
    }
  });

  let thumbnailTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(thumbnailTimer));

  const handleUrlInput = (value: string) => {
    setUrl(value);

    const result = detectVideoFromUrl(value);
    if (!result) return;

    const alreadyDetected = result.provider === getValue(videoForm, 'provider')
      && result.providerId === getValue(videoForm, 'provider_id');
    if (alreadyDetected) return;

    setValue(videoForm, 'provider', result.provider);
    setValue(videoForm, 'provider_id', result.providerId);
    setValue(videoForm, 'thumbnail_url', undefined);

    clearTimeout(thumbnailTimer);
    thumbnailTimer = setTimeout(async () => {
      setDetecting(true);
      const thumbnailUrl = await fetchThumbnailUrl(result.provider, result.providerId);
      setDetecting(false);
      setValue(videoForm, 'thumbnail_url', thumbnailUrl ?? undefined);
    }, 500);
  };

  const handleSubmit = (values: VideoForm) => {
    mutation.mutate(values);
  };

  const detectedProvider = () => getValue(videoForm, 'provider');
  const detectedProviderId = () => getValue(videoForm, 'provider_id');

  return (
    <Form onSubmit={handleSubmit} class={grid({ gap: '3', w: 'full' })}>
      <FormField name="provider_id">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Video URL</Field.Label>
            <Field.HelperText mt='-1' mb='1'>
              {detecting() ? 'Detecting video…' : "Paste a YouTube, Vimeo or Dailymotion link — we'll detect it automatically"}
            </Field.HelperText>
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=…"
              value={url()}
              onInput={(e) => handleUrlInput(e.currentTarget.value)}
            />
            <input type="hidden" />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Show when={detectedProvider() && detectedProviderId()}>
        <Box borderRadius='12px' overflow='hidden' bg='black' aspectRatio='16/9'>
          <VideoPlayer provider={detectedProvider() as any} provider_id={detectedProviderId()} />
        </Box>
      </Show>

      <FormField name="provider" type="string">
        {() => <input type="hidden" />}
      </FormField>
      <FormField name="thumbnail_url" type="string">
        {() => <input type="hidden" />}
      </FormField>

      <FormField name="title">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Title</Field.Label>
            <Input
              {...props}
              type="text"
              placeholder="Give it a clear headline…"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <FormField name="topic">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Topic</Field.Label>
            <RadioGroup.Root
              colorPalette="jade"
              value={field.value}
              onValueChange={(details) => setValue(videoForm, 'topic', details.value as any)}
            >
              <For each={TOPIC_KEYS}>
                {(topic) => (
                  <RadioGroup.Item value={topic}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText display='flex' alignItems='center' gap='2'>
                      <Box w='8px' h='8px' borderRadius='full' style={{ background: TOPICS[topic].color }} />
                      {TOPICS[topic].label}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                )}
              </For>
            </RadioGroup.Root>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <FormField name="description">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Why watch it?</Field.Label>
            <Field.HelperText mt='-1' mb='1'>Optional note to the community</Field.HelperText>
            <Textarea
              {...props}
              rows={4}
              placeholder="A line on what's worth watching here…"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Button
        type="submit"
        loading={mutation.isPending}
        colorPalette="jade"
        variant="solid"
        justifySelf="start"
      >
        <CheckIcon size={16} />
        {isEdit() ? 'Update Video' : 'Post to Go Watch'}
      </Button>
    </Form>
  );
}
